import { Color } from 'foundations-theming';
import { LoadingSpinnerProps, ShorthandSizeTypes } from './LoadingSpinner';
import { IconProps } from 'foundations-assets/icons/icon.types';

export const SIZE_OPTIONS: Record<ShorthandSizeTypes, string> = {
  xs: '20px',
  sm: '28px',
  md: '40px',
};

export const getSpinnerSize = (size: ShorthandSizeTypes = 'sm') => {
  return SIZE_OPTIONS[size];
};

export const getIconColor = ({
  use,
}: {
  use: NonNullable<LoadingSpinnerProps['use']>;
}): Color => {
  switch (use) {
    case 'danger':
      return 'fill-alert-default';
    case 'primary':
    case 'secondary':
    case 'secondary-alt':
      return 'fill-primary-default';
    case 'on-dark':
    case 'secondary-ghost':
      return 'fill-inverse-default-alt';
    case 'primary-white':
    case 'tertiary':
    case 'tertiary-light':
    case 'tertiary-extra-light':
    case 'tertiary-transparent':
      return 'icon-tertiary-default';
    case 'success':
      return 'fill-positive-default';
    case 'link':
      return 'icon-interactive-default';
    case 'primary-on-fill':
    case 'tertiary-on-fill':
      return 'icon-core-on-fill-default';
    default: {
      const __exhaustiveCheck: never = use;
      throw new Error(`Unhandled \`use\` argument:\n ${use}`);
    }
  }
};

export const getIconSize = (
  size: NonNullable<LoadingSpinnerProps['size']>
): NonNullable<IconProps['size']> => {
  switch (size) {
    case 'xs':
    case 'extra-small':
      return '500';
    case 'md':
    case 'medium':
      return '800';
    case 'sm':
    case 'small':
    default:
      return '700';
  }
};

/**
 * Computes the final spinner use based on the provided use and toolbar context.
 */
export const getComputedUse = ({
  use,
  isInFilledToolbar,
  isTrellis,
  isInLoadingButton = false,
}: {
  use: NonNullable<LoadingSpinnerProps['use']>;
  isInFilledToolbar: boolean;
  isTrellis: boolean;
  isInLoadingButton?: boolean;
}): NonNullable<LoadingSpinnerProps['use']> => {
  if (isInFilledToolbar) {
    // we want new secondary-alt buttons to render as secondary-ghost in canvas
    // secondary-alt (lavender) buttons are inverted in Canvas (orange on white) and look bad against dark toolbar fills
    if (!isTrellis) {
      if (use === 'secondary-alt') {
        return 'secondary-ghost';
      }
      return use;
    }

    // Custom mapping for buttons in a toolbar only in the Trellis theme
    // These mappings are defined by UX.
    // This is a temporary solution to avoid a `Button` `use` codemod/migration for buttons used in a toolbar.
    // These mappings are different from the `Button` mappings because the spinner/success icon has different colors.
    if (isTrellis) {
      // LoadingButton gets light background, needs dark icon
      // Standalone toolbar gets dark background, needs white (-on-fill) icon
      if (use === 'primary' || use === 'primary-white') {
        return isInLoadingButton ? 'primary' : 'primary-on-fill';
      }

      const tertiaryOnFillUses = [
        'secondary',
        'secondary-alt',
        'secondary-ghost',
        'on-dark',
        'tertiary',
        'tertiary-transparent',
        'tertiary-light',
        'tertiary-extra-light',
        'link',
      ];

      // LoadingButton gets light background, needs dark icon (tertiary)
      // Standalone toolbar gets dark background, needs white (-on-fill) icon
      if (tertiaryOnFillUses.includes(use)) {
        return isInLoadingButton ? 'tertiary' : 'tertiary-on-fill';
      }

      return use;
    }
  }

  return use;
};
